import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, Search, X, ChevronRight, HelpCircle } from 'lucide-react'
import { kbApi, type KBArticleListItem } from '../services/api'

const CATEGORY_LABELS: Record<string, string> = {
  'getting-started': 'Getting Started',
  auth: 'Authentication',
  'ticket-workflow': 'Ticket Workflow',
  attachments: 'Attachments',
  billing: 'Billing',
}

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const ALL_CATEGORIES = [
  { value: '', label: 'All topics' },
  ...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
]

function ArticleCard({ article }: { article: KBArticleListItem }) {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate(`/help/${article.id}`)}
      className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-brand-300 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <span className="inline-block text-xs font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full mb-2">
            {categoryLabel(article.category)}
          </span>
          <h3 className="text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors leading-snug">
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{article.excerpt}</p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-400 shrink-0 mt-0.5 transition-colors" />
      </div>
    </button>
  )
}

export function HelpCenter() {
  const [articles, setArticles] = useState<KBArticleListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')

  useEffect(() => {
    setLoading(true)
    kbApi
      .listArticles()
      .then((data) => {
        setArticles(data)
        setError(null)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load articles')
      })
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    let result = articles
    if (category) result = result.filter((a) => a.category === category)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          (a.excerpt ?? '').toLowerCase().includes(q) ||
          a.category.toLowerCase().includes(q)
      )
    }
    return result
  }, [articles, search, category])

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="w-5 h-5 text-brand-500" />
          <h1 className="text-xl font-semibold text-gray-900">Help Center</h1>
        </div>
        <p className="text-sm text-gray-500">Guides, how-tos, and answers to common questions.</p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none bg-white"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
        >
          {ALL_CATEGORIES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-16">
          <p className="text-red-600 mb-3 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-brand-500 hover:text-brand-600 font-medium"
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <HelpCircle className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">
            {search || category ? (
              <>
                No articles match your filters.{' '}
                <button
                  onClick={() => { setSearch(''); setCategory('') }}
                  className="text-brand-500 hover:text-brand-600 font-medium"
                >
                  Clear filters
                </button>
              </>
            ) : (
              'No articles available yet.'
            )}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </div>
  )
}
